// App.jsx (Root entry)
import React, { useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import {
  CssBaseline,
  ThemeProvider,
  Container,
  Alert,
  Box,
  Stack,
  CircularProgress,
  Typography,
} from "@mui/material";

import useArlTheme from "./theme/useArlTheme";
import { getDataUrl } from "./components/utils";
import AppInner from "./AppInner";
import {
  getDriverPoints,
  getDriverWins,
  getDriverPodiums,
  getCurrentDriverPoints,
  getCurrentDriverWins,
  getCurrentDriverPodiums,
  getDriverDropBreakdown,
} from "./utils/points";

/**
 * ===============================
 * Local cache for fetched JSON
 * ===============================
 * Cache Promise<json> per URL so the same file is never fetched twice
 * (and concurrent callers share the same in-flight request).
 */
const __appJsonPromiseCache = new Map(); // url -> Promise<any>
export function clearAppFetchCache() {
  __appJsonPromiseCache.clear();
}

async function fetchJsonUncached(url) {
  const res = await fetch(url, { cache: "no-store" });
  const ct = res.headers.get("content-type") || "";
  if (!res.ok || !ct.includes("application/json")) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Failed to load ${url} – status ${res.status} ${res.statusText}. Content-Type: ${
        ct || "unknown"
      }. Response starts with: ${text.slice(0, 120)}`
    );
  }
  return await res.json();
}

async function fetchJsonCached(url) {
  const key = String(url);

  if (__appJsonPromiseCache.has(key)) {
    return __appJsonPromiseCache.get(key);
  }

  const p = (async () => {
    return await fetchJsonUncached(key);
  })();

  __appJsonPromiseCache.set(key, p);

  // If it throws, allow a retry later.
  p.catch(() => {
    __appJsonPromiseCache.delete(key);
  });

  return p;
}

/**
 * Root App component
 * Handles theme, data loading, and routing shell
 */
export default function App() {
  const theme = useArlTheme();
  const [appData, setAppData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    (async () => {
      const url = getDataUrl();
      try {
        const json = await fetchJsonCached(url);
        await updateDriversPoints(json, () => active);
      } catch (e) {
        if (active) setError(e);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const updateDriversPoints = async (json, isActive = () => true) => {
    // NOTE: This function mutates json in-place (as your original did).
    // The caching improvement you asked for is on fetch; points utils now
    // also cache their file fetches, so this becomes much cheaper.

    for (const d of json.drivers.list) {
      // If the component unmounts mid-loop, stop early.
      if (!isActive()) return;

      const dACC = {...d};
      dACC.points = await getDriverPoints(d.id);
      dACC.wins = await getDriverWins(d.id);
      dACC.podiums = await getDriverPodiums(d.id);

      dACC.current_points = await getCurrentDriverPoints(d.id);
      const dropBreakdown = await getDriverDropBreakdown(d.id, 'ACC', true);
      dACC.current_points = dropBreakdown.points;
      dACC.gross_points = dropBreakdown.grossPoints;
      dACC.dropped_points = dropBreakdown.droppedPoints;
      dACC.dropped_round_files = dropBreakdown.droppedFiles;
      dACC.current_wins = await getCurrentDriverWins(d.id);
      dACC.current_podiums = await getCurrentDriverPodiums(d.id);
      
      json.standings["ACC"].drivers.push(dACC);

      const dAC = {...d};
      dAC.points = await getDriverPoints(d.id, "AC");
      dAC.wins = await getDriverWins(d.id, "AC");
      dAC.podiums = await getDriverPodiums(d.id, "AC");
      json.standings["AC"].drivers.push(dAC);
    }

    if (isActive()) setAppData(json);
  };

  const basename = process.env.PUBLIC_URL || "/";

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter basename={basename}>
        {error ? (
          <Container sx={{ py: 6 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {String(error.message)}
            </Alert>
            <Alert severity="info">
              Couldn't load <code>data.json</code>. Showing built-in defaults.
            </Alert>
            <AppInner appData={null} />
          </Container>
        ) : appData ? (
          <AppInner appData={appData} />
        ) : (
          <Box
            sx={{
              minHeight: "100dvh",
              display: "grid",
              placeItems: "center",
              p: 4,
            }}
          >
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading ARL data…
              </Typography>
            </Stack>
          </Box>
        )}
      </BrowserRouter>
    </ThemeProvider>
  );
}
